import React, { useState, useEffect, useContext, useCallback, memo } from "react";
import { ThemeContext } from "./ThemeContext.jsx";
import { getSpeciesColor, TYPE_SCALE } from "./theme.js";

// ─── THEMED UI COMPONENTS ────────────────────────────────────────────────────
// Themed channel header banners and glassmorphic notification toasts.
// Drop-in upgrades for existing UI elements.
//
// Usage:
//   <ThemedChannelHeader channelName="pack-grounds" description="..."
//     genre="werewolf" memberCount={5} />
//   <NotificationToast message="Cruz replied" type="dm"
//     characterName="Cruz" onDismiss={() => ...} />
//   <ToastContainer /> // manages toast stack

// ─── GENRE TO THEME MAPPING ─────────────────────────────────────────────────
const CHANNEL_THEMES = {
  // Auto-detect from channel name keywords
  pack: { gradient: "linear-gradient(135deg, #1a120a 0%, #0f1a0f 100%)", accent: "#ff9800", icon: "🐺" },
  wolf: { gradient: "linear-gradient(135deg, #1a120a 0%, #0f1a0f 100%)", accent: "#ff9800", icon: "🐺" },
  vampire: { gradient: "linear-gradient(135deg, #1a0808 0%, #0a0a0f 100%)", accent: "var(--ds-accent, #c62828)", icon: "🦇" },
  blood: { gradient: "linear-gradient(135deg, #1a0808 0%, #0a0a0f 100%)", accent: "var(--ds-accent, #c62828)", icon: "🩸" },
  siren: { gradient: "linear-gradient(135deg, #0a0f1a 0%, #0a1520 100%)", accent: "#3498db", icon: "🧜‍♀️" },
  ocean: { gradient: "linear-gradient(135deg, #0a0f1a 0%, #0a1520 100%)", accent: "#3498db", icon: "🌊" },
  fae: { gradient: "linear-gradient(135deg, #0a1a0f 0%, #0f1a18 100%)", accent: "#2ecc71", icon: "🧚" },
  fairy: { gradient: "linear-gradient(135deg, #0a1a0f 0%, #0f1a18 100%)", accent: "#2ecc71", icon: "✨" },
  olympus: { gradient: "linear-gradient(135deg, #1a0f1e 0%, #0f0a18 100%)", accent: "#9b59b6", icon: "⚡" },
  god: { gradient: "linear-gradient(135deg, #1a0f1e 0%, #0f0a18 100%)", accent: "#9b59b6", icon: "⚡" },
  romance: { gradient: "linear-gradient(135deg, #1a0a1e 0%, #1e0a18 100%)", accent: "#e91e63", icon: "💕" },
  love: { gradient: "linear-gradient(135deg, #1a0a1e 0%, #1e0a18 100%)", accent: "#e91e63", icon: "💕" },
  general: { gradient: "linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%)", accent: "var(--ds-gold, #c4a882)", icon: "💬" },
  ooc: { gradient: "linear-gradient(135deg, #0f0f14 0%, #1a1a20 100%)", accent: "#faa61a", icon: "🎭" },
};

const DEFAULT_CHANNEL_THEME = {
  gradient: "linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%)",
  accent: "#c4a882",
  icon: "💬",
};

function getChannelTheme(channelName = "") {
  const name = channelName.toLowerCase();
  for (const [key, theme] of Object.entries(CHANNEL_THEMES)) {
    if (name.includes(key)) return theme;
  }
  return DEFAULT_CHANNEL_THEME;
}

// ─── THEMED CHANNEL HEADER ───────────────────────────────────────────────────
export const ThemedChannelHeader = memo(function ThemedChannelHeader({
  channelName = "",
  description = "",
  memberCount = 0,
  genre, // optional override
  onBack,
  noButtonsOverlay = false, // when true, don't reserve padding-right for overlay buttons
}) {
  const { theme } = useContext(ThemeContext);
  const [isMobile] = useState(() => window.innerWidth < 768);
  const channelTheme = genre ? (CHANNEL_THEMES[genre] || DEFAULT_CHANNEL_THEME) : getChannelTheme(channelName);

  return (
    <div className="bq-bar" style={{
      position: "relative",
      padding: isMobile ? "10px 16px" : "16px 24px",
      // Reserve right space for the absolute action buttons (AI + theme + profile) — only on desktop or when overlay is used
      paddingRight: noButtonsOverlay ? (isMobile ? "16px" : "24px") : (isMobile ? "160px" : "220px"),
      background: channelTheme.gradient,
      borderBottom: `1px solid ${channelTheme.accent}22`,
      display: "flex",
      alignItems: "center",
      gap: "12px",
      overflow: "hidden",
      flexShrink: 0,
    }}>
      {/* Atmospheric glow */}
      <div style={{
        position: "absolute",
        top: 0, right: 0,
        width: "200px", height: "100%",
        background: `radial-gradient(ellipse at top right, ${channelTheme.accent}0a, transparent 70%)`,
        pointerEvents: "none",
      }} />
      {/* Bottom accent line */}
      <div style={{
        position: "absolute",
        bottom: 0, left: "10%", right: "10%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${channelTheme.accent}33, transparent)`,
      }} />

      {/* Back button (mobile) */}
      {onBack && (
        <button onClick={onBack} style={{
          background: "none", border: "none", color: theme.textMuted,
          fontSize: "20px", cursor: "pointer", padding: "8px",
          minWidth: "44px", minHeight: "44px", display: "flex", alignItems: "center", justifyContent: "center",
          WebkitTapHighlightColor: "rgba(255,255,255,0.1)",
        }}>←</button>
      )}

      {/* Channel icon */}
      <div style={{
        width: "36px", height: "36px",
        borderRadius: "10px",
        background: `linear-gradient(150deg, ${channelTheme.accent}22, var(--ds-gold-a12, rgba(212,175,55,0.08)))`,
        border: `1px solid var(--ds-gold-a55, rgba(212,175,55,0.34))`,
        boxShadow: `inset 0 0 0 1px var(--ds-gold-a14, rgba(212,175,55,0.08)), inset 0 0 12px -6px var(--ds-gold-a40, rgba(212,175,55,0.25))`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "18px",
        flexShrink: 0,
      }}>
        {channelTheme.icon}
      </div>

      {/* Channel info */}
      <div style={{ flex: 1, minWidth: 0, position: "relative", zIndex: 1 }}>
        <div style={{
          fontSize: isMobile ? TYPE_SCALE.base : TYPE_SCALE.md,
          fontWeight: 700,
          fontFamily: "'Cinzel', serif",
          color: "#fff",
          letterSpacing: "0.5px",
          display: "flex", alignItems: "center", gap: "8px",
          overflow: "hidden",
        }}>
          <span style={{ color: channelTheme.accent, fontSize: "14px", flexShrink: 0 }}>#</span>
          <span className="bq-title" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{channelName}</span>
        </div>
        {description && (
          <div style={{
            fontSize: TYPE_SCALE.xs,
            color: theme.textFaint,
            marginTop: "2px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {description}
          </div>
        )}
      </div>

      {/* Member count */}
      {memberCount > 0 && (
        <div style={{
          fontSize: "11px",
          color: theme.textFaint,
          display: "flex", alignItems: "center", gap: "4px",
          flexShrink: 0,
        }}>
          👥 {memberCount}
        </div>
      )}
    </div>
  );
});

// ─── NOTIFICATION TOAST ──────────────────────────────────────────────────────
export const NotificationToast = memo(function NotificationToast({
  message = "",
  type = "general", // "dm" | "channel" | "story" | "system" | "general"
  characterName,
  characterSpecies,
  avatarSrc,
  onDismiss,
  onTap,
  duration = 4000,
  id,
}) {
  const { theme } = useContext(ThemeContext);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss?.(id), 300);
  }, [id, onDismiss]);

  useEffect(() => {
    // Reveal: rAF gives a smooth slide-in, BUT rAF can silently never fire in a
    // backgrounded/occluded tab — which left the toast stuck at opacity:0
    // (mounted but invisible). The setTimeout fallback guarantees it appears.
    const raf = requestAnimationFrame(() => setVisible(true));
    const revealFallback = setTimeout(() => setVisible(true), 80);
    const timer = duration > 0 ? setTimeout(() => dismiss(), duration) : null;
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(revealFallback);
      if (timer) clearTimeout(timer);
    };
  }, [duration, dismiss]);

  const speciesColor = characterSpecies ? getSpeciesColor(characterSpecies) : "var(--ds-gold, #c4a882)";
  const typeColors = {
    dm: "#d1b3ff",
    channel: "#c4a882",
    story: "#2ecc71",
    system: "#faa61a",
    general: "#c4a882",
  };
  const accentColor = typeColors[type] || typeColors.general;

  return (
    <div
      onClick={() => { onTap?.(); dismiss(); }}
      style={{
        // position:relative so the accent bar below anchors to THIS toast, not
        // to the fixed stack container (which would misplace it when stacked).
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 14px",
        borderRadius: "var(--ds-r-md, 14px)",
        background: "var(--ds-glass, rgba(24,18,25,0.93))",
        backdropFilter: "blur(16px) saturate(1.3)",
        WebkitBackdropFilter: "blur(16px) saturate(1.3)",
        border: `1px solid ${accentColor}33`,
        boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px ${accentColor}11`,
        cursor: onTap ? "pointer" : "default",
        maxWidth: "360px",
        width: "100%",
        opacity: exiting ? 0 : visible ? 1 : 0,
        transform: exiting
          ? "translateX(120%)"
          : visible ? "translateX(0)" : "translateX(120%)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        pointerEvents: "all",
      }}
    >
      {/* Avatar */}
      {avatarSrc ? (
        <div style={{
          width: "36px", height: "36px",
          borderRadius: "50%", overflow: "hidden",
          border: `2px solid ${speciesColor}44`,
          flexShrink: 0,
        }}>
          <img src={avatarSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }} />
        </div>
      ) : (
        <div style={{
          width: "36px", height: "36px",
          borderRadius: "50%",
          background: `${accentColor}22`,
          border: `2px solid ${accentColor}44`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "16px", flexShrink: 0,
        }}>
          {type === "dm" ? "💬" : type === "channel" ? "#" : type === "story" ? "📖" : "🔔"}
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {characterName && (
          <div style={{
            fontSize: "12px", fontWeight: 700,
            color: speciesColor,
            fontFamily: "'Cinzel', serif",
            letterSpacing: "0.3px",
          }}>
            {characterName}
          </div>
        )}
        <div style={{
          fontSize: "12px", color: theme.textSecondary,
          overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          marginTop: characterName ? "1px" : 0,
        }}>
          {message}
        </div>
      </div>

      {/* Dismiss button */}
      <button
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        style={{
          background: "none", border: "none",
          color: theme.textFaint, fontSize: "14px",
          cursor: "pointer", padding: "4px",
          flexShrink: 0,
        }}
      >
        ✕
      </button>

      {/* Accent line */}
      <div style={{
        position: "absolute", left: 0, top: "15%", bottom: "15%",
        width: "3px", borderRadius: "2px",
        background: accentColor,
      }} />
    </div>
  );
});

// ─── TOAST CONTAINER (manages toast stack) ───────────────────────────────────
// Renders toast notifications in a fixed position stack.
// Usage:
//   const toastRef = useRef();
//   <ToastContainer ref={toastRef} />
//   toastRef.current.addToast({ message: "Hello", type: "dm" });
export const ToastContainer = React.forwardRef(function ToastContainer(props, ref) {
  const [toasts, setToasts] = useState([]);
  const [isMobile] = useState(() => window.innerWidth < 768);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev.slice(-4), { ...toast, id }]); // max 5 toasts
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Expose addToast to parent via ref
  React.useImperativeHandle(ref, () => ({ addToast, removeToast }), [addToast, removeToast]);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: "fixed",
      top: isMobile ? "60px" : "16px",
      right: isMobile ? "8px" : "16px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      zIndex: 9000,
      pointerEvents: "none",
      maxWidth: isMobile ? "calc(100vw - 16px)" : "360px",
    }}>
      {toasts.map(toast => (
        <NotificationToast
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          characterName={toast.characterName}
          characterSpecies={toast.characterSpecies}
          avatarSrc={toast.avatarSrc}
          duration={toast.duration}
          onDismiss={removeToast}
          onTap={toast.onTap}
        />
      ))}
    </div>
  );
});

export default { ThemedChannelHeader, NotificationToast, ToastContainer };
